import { useEffect, useState } from "react";
import { FaImage, FaVideo } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BulletInput,
  CardWrapper,
  CustomDropdown,
  DynamicSections,
  FloorPlan,
  ImageUploadSection,
  InputField,
  InputWithSelect,
  InputWithUnit,
  LocationSelector,
  MultiSelectSearch,
  PageHeader,
  PaymentScheduleInput,
  PdfUploadSection,
  RadioField,
  SmallInputField,
  SubmitButton,
  TextAreaField,
  VideoUploadSection,
} from "../../../../components/admin/common/form-elements";
import AddProjectPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddProjectPropertyPageSkeleton";
import api from "../../../../configs/api";
import { AMENITIES } from "../../../../utils/amenities";
import { INITIAL_LOCATION } from "../../../../utils/constants";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import { FiBriefcase, FiEdit, FiPlus } from "react-icons/fi";
import CompanyModal from "../../../../components/modals/CompanyModal";
import { useAuth } from "../../../../context/AuthContext";

const AddProjectPropertyPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.includes("edit");
  const navigate = useNavigate();
  const { user } = useAuth();
  const showCompanyDropdown = user?.role === "agent-builder";

  const initialFormData = {
    projectName: "",
    description: "",
    price: "",
    images: [],
    videoUrl: "",
    location: INITIAL_LOCATION,
    brochureUrl: "",
    facilities: [""],
    specification: "",
    totalFloors: "",
    totalArea: "",
    totalAreaUnit: "sqft",
    floorPlan: "",
    availability: "",
    ownership: "",
    amenities: [],
    paymentSchedule: [],
    brokerage: "",
    companyId: "",
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState("create");

  const brokerageOptions = [
    { label: "0% (No Brokerage)", value: 0 },
    { label: "0.5%", value: 0.5 },
    { label: "1%", value: 1 },
    { label: "1.5%", value: 1.5 },
    { label: "2%", value: 2 },
    { label: "2.5%", value: 2.5 },
    { label: "Custom / Negotiable", value: "custom" },
  ];

  //  FETCH DATA IN EDIT MODE
  useEffect(() => {
    if (!isEditMode || !id) return;

    const fetchProperty = async () => {
      try {
        setIsLoading(true);
        const res = await api.get(`/properties/${id}`);
        const data = res.data?.data;
        console.log("formdata:", data);
        if (!data) return;

        setFormData({
          projectName: data.title || "",
          description: data.description || "",
          price: data.property?.price || "",
          images: data.images || [],
          videoUrl: data.videos?.[0] || "",
          location: data.location || INITIAL_LOCATION,
          brochureUrl: data.property?.brochureUrl || "",
          facilities: data.property?.facilities,
          specification: data.property?.specification,
          totalFloors: data.property?.floors?.totalFloors || "",
          totalArea: data.property?.floors?.totalArea?.value || "",
          totalAreaUnit: data.property?.floors?.totalArea?.unit || "sqft",
          floorPlan: data.property?.floorPlan || "",
          availability: data.property?.availabilityStatus || "",
          ownership: data.property?.ownership || "",
          amenities: data.property?.amenities || [],
          paymentSchedule: data.property?.paymentSchedule || [],
          brokerage: data.property?.brokerage || "",
          companyId: data.property?.companyId || "",
        });

        setSelectedCompany(
          companies.find((c) => c._id === data.property?.companyId) || null
        );
      } catch (err) {
        showError("Failed to load property data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id, isEditMode, companies]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get(`/companies`);
      console.log("companies:", res.data.companies);
      setCompanies(res.data?.companies);
    } catch (error) {
      console.error("Failed to fetch companies", error);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const missing = [];

    if (!formData.projectName?.trim()) missing.push("Project Name");
    if (!formData.location?.coordinates?.length) missing.push("Location");
    if (!formData.price || formData?.price <= 0) missing.push("Price");
    if (!formData.images || formData.images.length < 4)
      missing.push("At least 5 Property Images");
    if (!formData.totalFloors || formData.totalFloors <= 0)
      missing.push("Total Floors");
    if (!formData.availability) missing.push("Availability Status");
    // if (!formData.companyId) missing.push("Company Details");

    if (missing.length) {
      showError(`Please fill out: ${missing[0]}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      title: formData?.projectName,
      description: formData?.description,
      type: "project",
      images: formData?.images,
      videos: [formData?.videoUrl],
      location: formData?.location,
      property: {
        price: formData?.price,
        brochureUrl: formData?.brochureUrl,
        facilities: formData?.facilities,
        specification: formData?.specification,
        floors: {
          totalFloors: formData?.totalFloors,
          totalArea: {
            value: formData?.totalArea,
            unit: formData?.totalAreaUnit,
          },
        },
        floorPlan: formData?.floorPlan,
        availabilityStatus: formData?.availability,
        ownership: formData?.ownership,
        amenities: formData?.amenities,
        paymentSchedule: formData?.paymentSchedule,
        brokerage: formData?.brokerage,
        companyId: formData?.companyId,
      },
    };

    // console.log("Payload :", payload);
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await api.put(`/properties/${id}`, payload);
        showSuccess("Property Updated Successfully");
      } else {
        await api.post(`/properties`, payload);
        showSuccess("Property Created Successfully");
      }

      navigate(-1);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          (isEditMode ? "Couldn't update property" : "Couldn't create property")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalSuccess = (company) => {
    setSelectedCompany(company);
    fetchCompanies();
  };

  if (isEditMode && isLoading) {
    return <AddProjectPropertyPageSkeleton />;
  }
  return (
    <div className="min-h-screen bg-background font-poppins">
      <PageHeader title={isEditMode ? "Edit Project" : "Add Project"} />

      <div className="max-full mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            <CardWrapper title={"Project Name"}>
              <InputField
                name="projectName"
                value={formData.projectName}
                placeholder="Project Name"
                onChange={handleInputChange}
              />
            </CardWrapper>

            <CardWrapper title="About your project">
              <VideoUploadSection
                key={formData.videoUrl || "new"}
                icon={FaVideo}
                prefillData={formData.videoUrl ? [formData.videoUrl] : []}
                onFileSelect={(videoUrl) =>
                  setFormData((prev) => ({ ...prev, videoUrl }))
                }
                buttonText="+ Upload Video"
              />
            </CardWrapper>

            <CardWrapper title="Add photos of your project">
              <ImageUploadSection
                key={id || "new"}
                icon={FaImage}
                prefillData={formData.images}
                onChange={(uploadedImages) =>
                  setFormData((prev) => ({ ...prev, images: uploadedImages }))
                }
                buttonText="↓ Add atleast 5 photos"
              />
            </CardWrapper>

            <CardWrapper title="Upload Brochure">
              <PdfUploadSection
                prefillData={formData.brochureUrl}
                onFileSelect={(brochureUrl) =>
                  setFormData((prev) => ({ ...prev, brochureUrl }))
                }
              />
            </CardWrapper>

            <CardWrapper title="Project Description">
              <TextAreaField
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </CardWrapper>
          </div>

          {/* MIDDLE COLUMN */}
          <div className="space-y-6">
            <CardWrapper>
              <BulletInput
                label="Project Facilities"
                name="facilities"
                placeholder="Add Facility"
                value={formData?.facilities}
                onChange={handleInputChange}
              />
            </CardWrapper>

            <CardWrapper>
              <DynamicSections
                enableAddNew={true}
                label="Add Specification"
                value={formData?.specification}
                onChange={(specification) =>
                  setFormData((prev) => ({ ...prev, specification }))
                }
              />
            </CardWrapper>

            <CardWrapper title="Floor Details">
              <div className="grid grid-cols-2 gap-2">
                <SmallInputField
                  value={formData.totalFloors}
                  name="totalFloors"
                  label={"Total Floors"}
                  placeholder="Total Floors"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      totalFloors: Number(e.target.value),
                    }))
                  }
                />
                <InputWithUnit
                  label={"Total Area"}
                  type="number"
                  value={formData.totalArea}
                  name="totalArea"
                  selectedUnit={formData.totalAreaUnit}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      totalArea: Number(e.target.value),
                    }))
                  }
                  disabled={false}
                  onUnitChange={(unit) =>
                    setFormData((prev) => ({ ...prev, totalAreaUnit: unit }))
                  }
                  placeholder="Total Area"
                />
              </div>
            </CardWrapper>

            <CardWrapper title="Floor Plan">
              <FloorPlan
                floorPlan={formData.floorPlan}
                onChange={(updatedUnits) =>
                  setFormData((prev) => ({ ...prev, floorPlan: updatedUnits }))
                }
              />
            </CardWrapper>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {showCompanyDropdown && (
              <CardWrapper title="Select Company">
                <CustomDropdown
                  value={formData.companyId}
                  onChange={(val) => {
                    const selected = companies.find((c) => c._id === val);
                    setSelectedCompany(selected || null);
                    setFormData((prev) => ({
                      ...prev,
                      companyId: val,
                    }));
                  }}
                  options={companies.map((c) => ({
                    value: c._id,
                    logo: c.companyLogo,
                    label: c.companyName,
                  }))}
                  placeholder="Choose existing company..."
                  className={
                    "border-gray-300 bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                  }
                />

                {/* Selected company preview (read-only) */}
                {selectedCompany && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                        {selectedCompany.companyLogo ? (
                          <img
                            src={selectedCompany.companyLogo}
                            className="w-full h-full object-cover"
                            alt="Company logo"
                          />
                        ) : (
                          <FiBriefcase className="text-gray-400 text-xl" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary mb-1">
                          {selectedCompany.companyName}
                        </p>
                        {selectedCompany.reraId && (
                          <p className="text-xs text-text-secondary">
                            RERA: {selectedCompany.reraId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="my-3 text-sm font-medium text-center">OR</div>

                {/* Open modal: create or edit */}
                <button
                  type="button"
                  onClick={() => {
                    setCompanyModalMode(selectedCompany ? "edit" : "create");
                    setIsCompanyModalOpen(true);
                  }}
                  className="text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
                >
                  <FiEdit className="w-4 h-4" />
                  {selectedCompany
                    ? "Edit Selected Company"
                    : "Create New Company"}
                </button>
                {selectedCompany && (
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyModalMode("create");
                      setIsCompanyModalOpen(true);
                    }}
                    className="mt-2 text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
                  >
                    <FiPlus className="w-4 h-4" />
                    Create New Company
                  </button>
                )}

                {/* Modal itself */}
                <CompanyModal
                  open={isCompanyModalOpen}
                  mode={companyModalMode}
                  initialData={
                    companyModalMode === "edit" ? selectedCompany : null
                  }
                  onClose={() => setIsCompanyModalOpen(false)}
                  onSuccess={handleModalSuccess}
                />
              </CardWrapper>
            )}

            <CardWrapper title="Availability Status">
              <RadioField
                name="availability"
                value={formData.availability}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, availability: value }))
                }
                options={[
                  { label: "Ready to move", value: "ready_to_move" },
                  { label: "Under Construction", value: "under_construction" },
                ]}
              />
            </CardWrapper>

            <CardWrapper title="Ownership">
              <RadioField
                name="ownership"
                value={formData.ownership}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, ownership: value }))
                }
                options={[
                  { label: "Freehold", value: "freehold" },
                  { label: "Co-operative society", value: "co_operative" },
                  { label: "Leasehold", value: "leasehold" },
                  { label: "Power of attorney", value: "power_of_attorney" },
                ]}
              />
            </CardWrapper>

            <CardWrapper title="Amenities">
              <MultiSelectSearch
                value={formData.amenities}
                onChange={(updated) =>
                  setFormData((prev) => ({ ...prev, amenities: updated }))
                }
                items={AMENITIES}
                labelKey="label"
                valueKey="key"
              />
            </CardWrapper>

            <CardWrapper title="Payment Schedule">
              <PaymentScheduleInput
                value={formData.paymentSchedule}
                onChange={(updated) =>
                  setFormData((prev) => ({ ...prev, paymentSchedule: updated }))
                }
              />
            </CardWrapper>

            <CardWrapper title="Price">
              <InputField
                type="number"
                value={formData.price}
                name="price"
                placeholder="Price"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    price: Number(e.target.value),
                  }))
                }
              />
            </CardWrapper>

            <CardWrapper title="Brokerage">
              <InputWithSelect
                placeholder={"Select Brokerage"}
                disabled={true}
                selectValue={formData.brokerage}
                selectOptions={brokerageOptions}
                onSelectChange={(value) =>
                  setFormData((prev) => ({ ...prev, brokerage: value }))
                }
              />
            </CardWrapper>

            <CardWrapper title="Location">
              <LocationSelector
                value={formData.location}
                onChange={(updatedLocation) =>
                  setFormData((prev) => ({
                    ...prev,
                    location: updatedLocation,
                  }))
                }
              />
            </CardWrapper>

            <SubmitButton
              disabled={!formData?.images?.length}
              isLoading={isSubmitting}
              onClick={handleSubmit}
              label={isEditMode ? "Save Changes" : "Add Property"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProjectPropertyPage;

// import { useEffect, useState } from "react";
// import { FaImage, FaVideo } from "react-icons/fa";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import {
//   BulletInput,
//   CardWrapper,
//   DynamicSections,
//   FloorPlan,
//   ImageUploadSection,
//   InputField,
//   InputWithSelect,
//   InputWithUnit,
//   LocationSelector,
//   MultiSelectSearch,
//   PageHeader,
//   PaymentScheduleInput,
//   PdfUploadSection,
//   RadioField,
//   SmallInputField,
//   SubmitButton,
//   TextAreaField,
//   VideoUploadSection,
// } from "../../../../components/admin/common/form-elements";
// import AddProjectPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddProjectPropertyPageSkeleton";
// import api from "../../../../configs/api";
// import { AMENITIES } from "../../../../utils/amenities";
// import { INITIAL_LOCATION } from "../../../../utils/constants";
// import { showError, showSuccess } from "../../../../utils/toastUtils";

// const AddProjectPropertyPage = () => {
//   const { id } = useParams();
//   const location = useLocation();
//   const isEditMode = location.pathname.includes("edit");
//   const navigate = useNavigate();

//   const initialFormData = {
//     projectName: "",
//     description: "",
//     price: "",
//     images: [],
//     videoUrl: "",
//     location: INITIAL_LOCATION,
//     brochureUrl: "",
//     facilities: [""],
//     specification: "",
//     totalFloors: "",
//     totalArea: "",
//     totalAreaUnit: "sqft",
//     floorPlan: "",
//     availability: "",
//     ownership: "",
//     amenities: [],
//     paymentSchedule: [],
//     brokerage: "",
//     builderName: "",
//   };

//   const [isLoading, setIsLoading] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [formData, setFormData] = useState(initialFormData);

//   const brokerageOptions = [
//     { label: "0% (No Brokerage)", value: 0 },
//     { label: "0.5%", value: 0.5 },
//     { label: "1%", value: 1 },
//     { label: "1.5%", value: 1.5 },
//     { label: "2%", value: 2 },
//     { label: "2.5%", value: 2.5 },
//     { label: "Custom / Negotiable", value: "custom" },
//   ];

//   //  FETCH DATA IN EDIT MODE
//   useEffect(() => {
//     if (!isEditMode || !id) return;

//     const fetchProperty = async () => {
//       try {
//         setIsLoading(true);
//         const res = await api.get(`/properties/${id}`);
//         const data = res.data?.data;
//         console.log("formdata:", data);
//         if (!data) return;

//         setFormData({
//           projectName: data.title || "",
//           description: data.description || "",
//           price: data.property?.price || "",
//           images: data.images || [],
//           videoUrl: data.videos?.[0] || "",
//           location: data.location || INITIAL_LOCATION,
//           brochureUrl: data.property?.brochureUrl || "",
//           facilities: data.property?.facilities,
//           specification: data.property?.specification,
//           totalFloors: data.property?.floors?.totalFloors || "",
//           totalArea: data.property?.floors?.totalArea?.value || "",
//           totalAreaUnit: data.property?.floors?.totalArea?.unit || "sqft",
//           floorPlan: data.property?.floorPlan || "",
//           availability: data.property?.availabilityStatus || "",
//           ownership: data.property?.ownership || "",
//           amenities: data.property?.amenities || [],
//           paymentSchedule: data.property?.paymentSchedule || [],
//           brokerage: data.property?.brokerage || "",
//           builderName: data.property?.builderName || "",
//         });
//       } catch (err) {
//         showError("Failed to load property data");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchProperty();
//   }, [id, isEditMode]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const validateForm = () => {
//     const missing = [];

//     if (!formData.projectName?.trim()) missing.push("Project Name");
//     if (!formData.location?.coordinates?.length) missing.push("Location");
//     if (!formData.price || formData?.price <= 0) missing.push("Price");
//     if (!formData.images || formData.images.length < 4)
//       missing.push("At least 5 Property Images");
//     if (!formData.totalFloors || formData.totalFloors <= 0)
//       missing.push("Total Floors");
//     if (!formData.availability) missing.push("Availability Status");

//     if (missing.length) {
//       showError(`Please fill out: ${missing[0]}`);
//       return false;
//     }
//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     const payload = {
//       title: formData?.projectName,
//       description: formData?.description,
//       type: "project",
//       images: formData?.images,
//       videos: [formData?.videoUrl],
//       location: formData?.location,
//       property: {
//         price: formData?.price,
//         brochureUrl: formData?.brochureUrl,
//         facilities: formData?.facilities,
//         specification: formData?.specification,
//         floors: {
//           totalFloors: formData?.totalFloors,
//           totalArea: {
//             value: formData?.totalArea,
//             unit: formData?.totalAreaUnit,
//           },
//         },
//         floorPlan: formData?.floorPlan,
//         availabilityStatus: formData?.availability,
//         ownership: formData?.ownership,
//         amenities: formData?.amenities,
//         paymentSchedule: formData?.paymentSchedule,
//         brokerage: formData?.brokerage,
//         builderName: formData?.builderName,
//       },
//     };
//     console.log("Payload :", payload);
//     setIsSubmitting(true);
//     try {
//       if (isEditMode) {
//         await api.put(`/properties/${id}`, payload);
//         showSuccess("Property Updated Successfully");
//       } else {
//         await api.post(`/properties`, payload);
//         showSuccess("Property Created Successfully");
//       }

//       navigate(-1);
//     } catch (error) {
//       showError(
//         error?.response?.data?.message ||
//           (isEditMode ? "Couldn't update property" : "Couldn't create property")
//       );
//     } finally {
//       setIsSubmitting(false);
//     }
//   };
//   if (isEditMode && isLoading) {
//     return <AddProjectPropertyPageSkeleton />;
//   }
//   return (
//     <div className="min-h-screen bg-background font-poppins">
//       <PageHeader title={isEditMode ? "Edit Project" : "Add Project"} />

//       <div className="max-full mx-auto px-4 md:px-6 py-6">
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
//           {/* LEFT COLUMN */}
//           <div className="space-y-6">
//             <CardWrapper title={"Project Name"}>
//               <InputField
//                 name="projectName"
//                 value={formData.projectName}
//                 placeholder="Project Name"
//                 onChange={handleInputChange}
//               />
//             </CardWrapper>

//             <CardWrapper title="About your project">
//               <VideoUploadSection
//                 key={formData.videoUrl || "new"}
//                 icon={FaVideo}
//                 prefillData={formData.videoUrl ? [formData.videoUrl] : []}
//                 onFileSelect={(videoUrl) =>
//                   setFormData((prev) => ({ ...prev, videoUrl }))
//                 }
//                 buttonText="+ Upload Video"
//               />
//             </CardWrapper>

//             <CardWrapper title="Add photos of your project">
//               <ImageUploadSection
//                 key={id || "new"}
//                 icon={FaImage}
//                 prefillData={formData.images}
//                 onChange={(uploadedImages) =>
//                   setFormData((prev) => ({ ...prev, images: uploadedImages }))
//                 }
//                 buttonText="↓ Add atleast 5 photos"
//               />
//             </CardWrapper>

//             <CardWrapper title="Upload Brochure">
//               <PdfUploadSection
//                 prefillData={formData.brochureUrl}
//                 onFileSelect={(brochureUrl) =>
//                   setFormData((prev) => ({ ...prev, brochureUrl }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Project Description">
//               <TextAreaField
//                 value={formData.description}
//                 onChange={(e) =>
//                   setFormData((prev) => ({
//                     ...prev,
//                     description: e.target.value,
//                   }))
//                 }
//               />
//             </CardWrapper>
//           </div>

//           {/* MIDDLE COLUMN */}
//           <div className="space-y-6">
//             <CardWrapper>
//               <BulletInput
//                 label="Project Facilities"
//                 name="facilities"
//                 placeholder="Add Facility"
//                 value={formData?.facilities}
//                 onChange={handleInputChange}
//               />
//             </CardWrapper>

//             <CardWrapper>
//               <DynamicSections
//                 enableAddNew={true}
//                 label="Add Specification"
//                 value={formData?.specification}
//                 onChange={(specification) =>
//                   setFormData((prev) => ({ ...prev, specification }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Floor Details">
//               <div className="grid grid-cols-2 gap-2">
//                 <SmallInputField
//                   value={formData.totalFloors}
//                   name="totalFloors"
//                   label={"Total Floors"}
//                   placeholder="Total Floors"
//                   onChange={(e) =>
//                     setFormData((prev) => ({
//                       ...prev,
//                       totalFloors: Number(e.target.value),
//                     }))
//                   }
//                 />
//                 <InputWithUnit
//                   label={"Total Area"}
//                   type="number"
//                   value={formData.totalArea}
//                   name="totalArea"
//                   selectedUnit={formData.totalAreaUnit}
//                   onChange={(e) =>
//                     setFormData((prev) => ({
//                       ...prev,
//                       totalArea: Number(e.target.value),
//                     }))
//                   }
//                   disabled={false}
//                   onUnitChange={(unit) =>
//                     setFormData((prev) => ({ ...prev, totalAreaUnit: unit }))
//                   }
//                   placeholder="Total Area"
//                 />
//               </div>
//             </CardWrapper>

//             <CardWrapper title="Floor Plan">
//               <FloorPlan
//                 floorPlan={formData.floorPlan}
//                 onChange={(updatedUnits) =>
//                   setFormData((prev) => ({ ...prev, floorPlan: updatedUnits }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Builder Name">
//               <InputField
//                 name="builderName"
//                 value={formData.builderName}
//                 placeholder="Builder Name"
//                 onChange={handleInputChange}
//               />
//             </CardWrapper>
//           </div>

//           {/* RIGHT COLUMN */}
//           <div className="space-y-6">
//             <CardWrapper title="Availability Status">
//               <RadioField
//                 name="availability"
//                 value={formData.availability}
//                 onChange={(value) =>
//                   setFormData((prev) => ({ ...prev, availability: value }))
//                 }
//                 options={[
//                   { label: "Ready to move", value: "ready_to_move" },
//                   { label: "Under Construction", value: "under_construction" },
//                 ]}
//               />
//             </CardWrapper>

//             <CardWrapper title="Ownership">
//               <RadioField
//                 name="ownership"
//                 value={formData.ownership}
//                 onChange={(value) =>
//                   setFormData((prev) => ({ ...prev, ownership: value }))
//                 }
//                 options={[
//                   { label: "Freehold", value: "freehold" },
//                   { label: "Co-operative society", value: "co_operative" },
//                   { label: "Leasehold", value: "leasehold" },
//                   { label: "Power of attorney", value: "power_of_attorney" },
//                 ]}
//               />
//             </CardWrapper>

//             <CardWrapper title="Amenities">
//               <MultiSelectSearch
//                 value={formData.amenities}
//                 onChange={(updated) =>
//                   setFormData((prev) => ({ ...prev, amenities: updated }))
//                 }
//                 items={AMENITIES}
//                 labelKey="label"
//                 valueKey="key"
//               />
//             </CardWrapper>

//             <CardWrapper title="Payment Schedule">
//               <PaymentScheduleInput
//                 value={formData.paymentSchedule}
//                 onChange={(updated) =>
//                   setFormData((prev) => ({ ...prev, paymentSchedule: updated }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Price">
//               <InputField
//                 type="number"
//                 value={formData.price}
//                 name="price"
//                 placeholder="Price"
//                 onChange={(e) =>
//                   setFormData((prev) => ({
//                     ...prev,
//                     price: Number(e.target.value),
//                   }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Brokerage">
//               <InputWithSelect
//                 placeholder={"Select Brokerage"}
//                 disabled={true}
//                 selectValue={formData.brokerage}
//                 selectOptions={brokerageOptions}
//                 onSelectChange={(value) =>
//                   setFormData((prev) => ({ ...prev, brokerage: value }))
//                 }
//               />
//             </CardWrapper>

//             <CardWrapper title="Location">
//               <LocationSelector
//                 value={formData.location}
//                 onChange={(updatedLocation) =>
//                   setFormData((prev) => ({
//                     ...prev,
//                     location: updatedLocation,
//                   }))
//                 }
//               />
//             </CardWrapper>

//             <SubmitButton
//               disabled={!formData?.images?.length}
//               isLoading={isSubmitting}
//               onClick={handleSubmit}
//               label={isEditMode ? "Save Changes" : "Add Property"}
//             />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddProjectPropertyPage;
